import { useState } from 'react';
import cx from 'classnames';
import { Icon } from '@iconify/react';
import { useSortableTable } from './AdministratorDashboard/Tables/Hooks/useSortableTable';
import tableStyles from './AdministratorDashboard/Tables/Table.module.scss';
import Tooltip from './components/Tooltip';
import { getEmotionColor, getGradeColumnLabel } from './ConcerningEmotionsReportUtilities';
import { EmotionsReportDisplayRow } from './ConcerningEmotionsReportRows';
import { getMoodDisplayName } from '@peekapak/types/Mood';
import StudentDetailPanel from './StudentDetailPanel';
import styles from './ConcerningEmotionsReport.module.scss';

interface Props {
  rows: EmotionsReportDisplayRow[];
  showTeacherColumn?: boolean;
}

const ConcerningEmotionsReportTable = ({ rows, showTeacherColumn }: Props): JSX.Element => {
  const [sortedRows, handleSorting] = useSortableTable(rows) as [
    EmotionsReportDisplayRow[],
    (sortField: string, sortOrder: string) => void,
  ];

  const [selectedRow, setSelectedRow] = useState<EmotionsReportDisplayRow | null>(null);

  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const toggleSort = (field: string) => {
    const nextOrder = field === sortField && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(nextOrder);
    handleSorting(field, nextOrder);
  };

  return (
    <>
      <table className={tableStyles.tableContainer}>
        <thead>
          <tr>
            <th style={{ width: '16%' }}>Name</th>
            <th style={{ width: '20%' }}>3 most recent down mood check-ins</th>
            <th className={styles.centered} style={{ width: '20%' }}>
              Down moods (30 days)
              <button
                type='button'
                className={styles.sortButton}
                aria-label='Sort by down moods in the last 30 days'
                onClick={() => toggleSort('last30DaysLowCount')}
              >
                <Icon className={styles.sortIcon} icon='bxs:down-arrow' />
              </button>
            </th>
            <th className={styles.centered} style={{ width: '15%' }}>
              Down / Total
              <Tooltip
                content='Down check-ins / total check-ins since class was created'
                direction='top'
                color='white'
                maxWidth='160px'
              >
                <p className={tableStyles.tooltipIcon}>?</p>
              </Tooltip>
            </th>
            {showTeacherColumn && <th style={{ width: '9%' }}>Teacher</th>}
            <th style={{ width: '12%' }}>
              Attention
              <Tooltip
                content='Urgency level based on check-in patterns.'
                direction='top'
                color='white'
                maxWidth='160px'
              >
                <p className={tableStyles.tooltipIcon}>?</p>
              </Tooltip>
            </th>
            <th className={styles.centered} style={{ width: '8%' }}>
              Grade
              <button
                type='button'
                className={styles.sortButton}
                aria-label='Sort by grade'
                onClick={() => toggleSort('grade')}
              >
                <Icon className={styles.sortIcon} icon='bxs:down-arrow' />
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row) => (
            <tr key={row.userId} className={styles.clickableRow}>
              <td className={row.urgencyTier === 'urgent' ? styles.urgentName : undefined}>
                {row.urgencyTier === 'urgent' && <Icon className={styles.urgentIcon} icon='mdi:alert-circle' />}
                <button
                  type='button'
                  className={styles.rowTrigger}
                  onClick={() => setSelectedRow(row)}
                  aria-label={`Open details for ${row.name}`}
                >
                  {row.name}
                  <Icon className={styles.rowChevron} icon='mdi:chevron-right' />
                </button>
              </td>
              <td className={styles.recentFeelingsCell}>
                {row.recentFeelings.map((feeling, index) => {
                  const color = getEmotionColor(feeling, row.gradeBand);
                  return (
                    <span
                      key={index}
                      className={styles.tag}
                      style={{ backgroundColor: color.backgroundColor, color: color.textColor }}
                    >
                      {getMoodDisplayName(feeling)}
                    </span>
                  );
                })}
              </td>
              <td className={cx(styles.centered, row.urgencyTier === 'urgent' && styles.urgentName)}>
                {row.last30DaysLowCount}
              </td>
              <td className={styles.centered}>
                {row.downCount}/{row.totalCount}
              </td>
              {showTeacherColumn && <td className={styles.teacherName}>{row.teacherName}</td>}
              <td className={cx(styles[row.urgencyTier ?? 'none'], styles.attentionCell)}>{row.urgencyTier ?? '-'}</td>
              <td className={styles.centered}>{getGradeColumnLabel(row.grade)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {selectedRow && <StudentDetailPanel row={selectedRow} onClose={() => setSelectedRow(null)} />}
    </>
  );
};

export default ConcerningEmotionsReportTable;
